import { useQuery } from '@tanstack/react-query';
import { usePaginatedQuery } from '../../../hooks/use-paginated-query';
import type { PriceListApiResponse, PriceListSource } from '../types';
import { usePriceListsApi } from './use-price-lists-api';

/**
 * Query key for usePaginatedPriceLists
 */
export const QK_PAGINATED_PRICE_LISTS = () => ['paginated-price-lists'] as const;

/**
 * Query key for useAllPriceLists
 */
export const QK_ALL_PRICE_LISTS = () => ['all-price-lists'] as const;

/**
 * Query key for usePriceList
 */
export const QK_GET_PRICE_LIST = (slug: string) => ['price-list', slug] as const;

export function usePaginatedPriceLists(sources: PriceListSource[]) {
  const { priceListsApi } = usePriceListsApi();

  const query = usePaginatedQuery<PriceListApiResponse, void>(undefined, {
    queryKey: [...QK_PAGINATED_PRICE_LISTS(), ...sources],
    queryFn: (_, paginationOpts) => priceListsApi.listPaginated(sources, paginationOpts),
  });

  return {
    ...query,
    data: query.data ?? [],
  };
}

export function useAllPriceListsWithManualPriceCounts() {
  const { priceListsApi } = usePriceListsApi();

  const query = useQuery<PriceListApiResponse[]>({
    queryKey: [...QK_ALL_PRICE_LISTS(), 'with-manual-counts'],
    queryFn: () => priceListsApi.listAllWithManualPriceCounts(),
  });

  return {
    ...query,
    data: query.data ?? [],
  };
}

/**
 * Hook to fetch all price lists.
 */
export function useAllPriceLists() {
  const { priceListsApi } = usePriceListsApi();

  const query = useQuery<PriceListApiResponse[]>({
    queryKey: [...QK_ALL_PRICE_LISTS()],
    queryFn: () => priceListsApi.listAll(),
  });

  return {
    ...query,
    data: query.data ?? [],
  };
}
/**
 * Hook to fetch all paginated price lists.
 */
export function usePaginatedAllPriceLists(sources: PriceListSource[]) {
  const { priceListsApi } = usePriceListsApi();

  const query = usePaginatedQuery<PriceListApiResponse, void>(undefined, {
    queryKey: [...QK_ALL_PRICE_LISTS(), ...sources],
    queryFn: (_, paginationOpts) => priceListsApi.listAllPaginated(sources, paginationOpts),
  });

  return {
    ...query,
    data: query.data ?? [],
  };
}
